import { useState } from "react";
import { Link } from "react-router";
import translations from '../locales/translations.json';

function t(key) {
  return translations[key] || key;
}

const digitsOnly = (value) => (value || '').replace(/\D/g, '');

// Luhn checksum, used by both personnummer and organisationsnummer
const luhnValid = (digits) => {
    let sum = 0;
    for (let i = 0; i < digits.length; i++) {
        let n = Number(digits[i]);
        if (i % 2 === 0) {
            n *= 2;
            if (n > 9) n -= 9;
        }
        sum += n;
    }
    return sum % 10 === 0;
};

// Each validator returns a translation key for the error, or null if the value is valid
const validators = {
    name: (value) => {
        if (!value.trim()) return 'field_required';
        if (value.trim().split(/\s+/).length < 2) return 'full_name_required';
        return null;
    },
    email: (value) => {
        if (!value.trim()) return 'field_required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim())) return 'email_invalid';
        return null;
    },
    password: (value) => {
        if (!value) return 'field_required';
        if (value.length < 8) return 'password_too_short';
        return null;
    },
    'password-confirm': (value, values) => {
        if (!value) return 'field_required';
        if (value !== values.password) return 'passwords_do_not_match';
        return null;
    },
    // Swedish number: 0 or +46 followed by 7-9 digits; spaces and hyphens allowed
    phone: (value) => {
        if (!value.trim()) return 'field_required';
        const compact = value.replace(/[\s-]/g, '');
        if (!/^(?:\+46|0)[1-9]\d{6,8}$/.test(compact)) return 'phone_invalid';
        return null;
    },
    // 10 digits (mirrors server) with a valid check digit; spaces and hyphens allowed
    organisationsnummer: (value) => {
        if (!value.trim()) return 'field_required';
        if (!/^[\d\s-]+$/.test(value) || digitsOnly(value).length !== 10) return 'organisationsnummer_invalid_format';
        if (!luhnValid(digitsOnly(value))) return 'organisationsnummer_invalid_checksum';
        return null;
    },
    adress: (value) => (value.trim() ? null : 'field_required'),
    postnummer: (value) => {
        if (!value.trim()) return 'field_required';
        if (!/^\d{3}\s?\d{2}$/.test(value.trim())) return 'postal_code_invalid';
        return null;
    },
    postort: (value) => (value.trim() ? null : 'field_required'),
    bank_account: (value) => {
        if (!value.trim()) return 'field_required';
        if (!/^[\d\s-]+$/.test(value.trim())) return 'bank_account_invalid';
        const digitCount = digitsOnly(value).length;
        if (digitCount < 7 || digitCount > 17) return 'bank_account_invalid_length';
        return null;
    },
    // letters only (words may be separated by a space or hyphen), at least 3 letters
    bank_account_name: (value) => {
        const trimmed = value.trim();
        if (!trimmed) return 'field_required';
        if (!/^\p{L}+(?:[\s-]+\p{L}+)*$/u.test(trimmed) || trimmed.replace(/[\s-]/g, '').length < 3) return 'bank_account_name_invalid';
        return null;
    },
};

const validateAll = (values) => {
    const errors = {};
    for (const [field, validate] of Object.entries(validators)) {
        const errorKey = validate(values[field] || '', values);
        if (errorKey) errors[field] = t(errorKey);
    }
    return errors;
};

const formValues = (form) => Object.fromEntries(new FormData(form).entries());

function Element({ setUser, apiCall, setCookie, setToken }) {

    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [serverErrors, setServerErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [momsad, setMomsad] = useState(false);

    const authenticate = async (data) => {

        console.log( 'authenticating with data:', data );

        const { response, error } = await apiCall("POST", "login", {
            email : data.email,
            password: data.password
        });

        if (error) {
            console.error(response);
            setError(error);
            return;
        }

        if (response && response.user) {
            setCookie('token', response.token, { path: '/' });
            // setCookie('user', response.user, { path: '/' });
            setUser(response.user);
            setToken(response.token);
            window.location.href = '/add';
        }
    };

    // revalidate on every change so an error disappears as soon as the input is corrected
    const handleChange = (e) => {
        const { name } = e.target;
        setFieldErrors(validateAll(formValues(e.currentTarget)));
        if (serverErrors[name]) {
            setServerErrors(({ [name]: _, ...rest }) => rest);
        }
    };

    // show a field's error first once the user has left it
    const handleBlur = (e) => {
        const { name } = e.target;
        if (!validators[name] || touched[name]) return;
        setTouched((prev) => ({ ...prev, [name]: true }));
        setFieldErrors(validateAll(formValues(e.currentTarget)));
    };

    const fieldError = (name) => serverErrors[name] || (touched[name] ? fieldErrors[name] : null);

    const inputClass = (name) => {
        if (fieldError(name)) return 'form-control is-invalid';
        if (touched[name]) return 'form-control is-valid';
        return 'form-control';
    };

    const feedback = (name) => fieldError(name) && <div className="invalid-feedback d-block">{fieldError(name)}</div>;

    // send data to API on form submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        let data = formValues(e.target);

        // show every field's error and stop if anything is invalid
        const errors = validateAll(data);
        setFieldErrors(errors);
        setTouched(Object.fromEntries(Object.keys(validators).map((field) => [field, true])));
        const firstInvalid = Object.keys(validators).find((field) => errors[field]);
        if (firstInvalid) {
            e.target.elements[firstInvalid].focus();
            setError(t('form_has_errors'));
            return;
        }

        // if interested_in_meat_return is set to on, set it to true
        if (data.interested_in_meat_return === 'on') {
            data.interested_in_meat_return = true;
        } else {
            data.interested_in_meat_return = false;
        }

        // remove password-confirm from data
        delete data['password-confirm'];

        // check if terms are accepted
        if (!data.terms) {
            setError(t('accept_terms_conditions'));
            return;
        }

        const { response, error, errors: apiErrors } = await apiCall("POST", "register", data);

        if (error) {
            if (apiErrors) {
                setServerErrors(Object.fromEntries(
                    Object.entries(apiErrors).map(([field, messages]) => [field, messages[0]])
                ));
            }
            setError(error);
            return;
        }

        if (response && response.user) {
            //wait before redirecting to login
            setTimeout(() => {
                authenticate(data);
            }, 1000);
        } else {
            setError(t('login_failed'));
        }

    };


    return (
        <div className="row m-0">
            <div className="col-12 p-0">
                <div className="login-card login-dark">

                    <div className="login-main create-account">
                        <form className="theme-form" onSubmit={handleSubmit} onChange={handleChange} onBlur={handleBlur} noValidate>
                            <h4>{t('create_your_account')}</h4>
                            <p>{t('enter_personal_details')}</p>
                            <div className="form-group">
                                <label className="col-form-label pt-0">{t('your_name')}</label>
                                <input className={inputClass('name')} type="text" required name="name" autoComplete="name" placeholder={t('first_name_placeholder')} />
                                {feedback('name')}
                            </div>
                            <div className="form-group">
                                <label className="col-form-label">{t('email_address')}</label>
                                <input className={inputClass('email')} type="email" required name="email" autoComplete="email" placeholder={t('email_placeholder')} />
                                {feedback('email')}
                            </div>
                            <div className="form-group">
                                <label className="col-form-label">{t('password')}</label>
                                <div className="form-input position-relative">
                                    <input className={inputClass('password')} type="password" name="password" required autoComplete="new-password" placeholder={t('password_placeholder')} />
                                    {feedback('password')}
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="col-form-label">{t('confirm_password')}</label>
                                <div className="form-input position-relative">
                                    <input className={inputClass('password-confirm')} type="password" name="password-confirm" required autoComplete="new-password" placeholder={t('password_placeholder')} />
                                    {feedback('password-confirm')}
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="col-form-label">{t('phone_number')}</label>
                                <input className={inputClass('phone')} type="tel" required name="phone" autoComplete="tel" placeholder={t('phone_placeholder')} />
                                {feedback('phone')}
                            </div>
                            <div className="form-group">
                                <div className="form-check form-switch">
                                    <input className="form-check-input" id="interestedInMeatReturn" type="checkbox" role="switch" name="interested_in_meat_return" />
                                    <label className="form-check-label" htmlFor="interestedInMeatReturn">{t('interested_in_meat_return')}</label>
                                </div>
                            </div>

                            <div className="form-group">
                                <div className="form-check form-switch">
                                    <input className="form-check-input" id="Momsad" type="checkbox" role="switch" name="momsad" checked={momsad} onChange={() => setMomsad(!momsad)} />
                                    <label className="form-check-label" htmlFor="Momsad">{t('momsad')}</label>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="col-form-label">{t('organisationsnummer')}</label>
                                <input
                                    className={inputClass('organisationsnummer')}
                                    type="text"
                                    required
                                    name="organisationsnummer"
                                    inputMode="numeric"
                                    placeholder={t('organisationsnummer_placeholder')}
                                />
                                <small className="form-text text-muted">{t('organisationsnummer_helper')}</small>
                                {feedback('organisationsnummer')}
                            </div>


                            <div className="form-group">
                                <label className="col-form-label">{t('address')}</label>
                                <input className={inputClass('adress')} type="text" required name="adress" autoComplete="street-address" placeholder={t('address_placeholder')} />
                                {feedback('adress')}
                            </div>
                            <div className="form-group">
                                <label className="col-form-label">{t('postal_code')}</label>
                                <input className={inputClass('postnummer')} type="text" required name="postnummer" inputMode="numeric" autoComplete="postal-code" placeholder={t('postal_code_placeholder')} />
                                {feedback('postnummer')}
                            </div>
                            <div className="form-group">
                                <label className="col-form-label">{t('city')}</label>
                                <input className={inputClass('postort')} type="text" required name="postort" autoComplete="address-level2" placeholder={t('city_placeholder')} />
                                {feedback('postort')}
                            </div>

                            <div className="form-group">
                                <label className="col-form-label">{t('bank_account')}</label>
                                <input className={inputClass('bank_account')} type="text" required name="bank_account" placeholder={t('bank_account_placeholder')} />
                                {feedback('bank_account')}
                            </div>
                            <div className="form-group">
                                <label className="col-form-label">{t('bank_account_name')}</label>
                                <input className={inputClass('bank_account_name')} type="text" required name="bank_account_name" placeholder={t('bank_account_name_placeholder')} />
                                {feedback('bank_account_name')}
                            </div>
                            <div className="form-group mb-0">
                                <div className="form-check">
                                    <input className="checkbox-primary form-check-input" id="checkbox1" type="checkbox" name="terms" />
                                    <label className="text-muted form-check-label" htmlFor="checkbox1">{t('agree_terms_conditions')} <a className="ms-2" href="/terms">{t('privacy_policy')}</a></label>
                                </div>
                                <button className="btn btn-primary btn-block w-100 mt-3" type="submit">{t('create_account')}</button>
                            </div>
                            {error && <div className="alert alert-danger mt-2">{error}</div>}
                            <p className="mt-4 mb-0">{t('already_have_account')}<Link className="ms-2" to="/login">{t('sign_in')}</Link></p>
                        </form>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default Element;
